/**
 * The transcripts API, as a host sees it — issue #30, epic #19.
 *
 * The backend half is `packages/kvox-api/src/transcripts/transcripts.controller.ts` and
 * `dto/transcript.dto.ts`; every type below is a hand-written mirror of a Zod
 * schema in that file, named identically so the two can be diffed by eye. Every
 * call takes the {@link KvoxWebHost} first and goes through the `kvox*` helpers
 * of the core slice, so the transport (the bearer token, the one-shot
 * 401 → refresh → retry, the maintenance recogniser) stays the host's.
 *
 * =============================================================================
 * TWO OF THE ELEVEN CALLS ARE CONDITIONAL GETS, AND THAT IS THE POINT
 * =============================================================================
 *
 * `GET /:id` and `GET /:id/segments` answer a weak ETag (`W/"v<n>"`) and a
 * `304` to a matching `If-None-Match`. A plain JSON request returns the parsed
 * payload and nothing else, so a caller can never see the `ETag` header it
 * would have to send back, and a `304` carries no body to unwrap. Those two
 * calls therefore use `host.http.requestWithMeta` (`kvoxGetWithMeta`), which
 * reports the status and the headers and resolves on a `304`.
 *
 * That matters because `useTranscript` polls those two routes every five
 * seconds while a transcript is processing. Without the validator, every poll
 * ships the full detail payload — speakers, statuses, the lot — for an answer
 * that has not moved; with it, the common case costs a request line and a
 * header block.
 *
 * `conditionalGet` below is NARROW: it adds the `If-None-Match` header when it
 * has a validator and folds the answer into {@link ConditionalResult}. The 401
 * path and the URL base are the host's, so a token that expires mid-poll is
 * refreshed by the ONE implementation that knows how.
 */
import { kvoxDelete, kvoxGet, kvoxGetWithMeta, kvoxPatch, kvoxPost } from '../../../core/headless/index.js';
async function conditionalGet(host, endpoint, etag) {
    // Sent only when we HAVE one. An `If-None-Match: null` would be a
    // syntactically valid header the server compares against and never
    // matches — a poll that silently lost its own optimisation.
    const response = await kvoxGetWithMeta(host, endpoint, {
        ...(etag ? { headers: { 'If-None-Match': etag } } : {}),
    });
    if (response.status === 304)
        return { status: 'not-modified' };
    return {
        status: 'ok',
        data: response.data,
        etag: response.headers.get('ETag'),
    };
}
// =============================================================================
// Reads
// =============================================================================
/**
 * `GET /api/transcripts`.
 *
 * @stability experimental
 */
export async function getTranscripts(host, params = {}) {
    const query = {};
    if (params.scope)
        query.scope = params.scope;
    if (params.status)
        query.status = params.status;
    // Trimmed-empty is omitted rather than sent: `q=` is a filter the API would
    // apply, and "title contains the empty string" is not what the user meant by
    // clearing the box.
    if (params.q && params.q.trim())
        query.q = params.q.trim();
    if (params.cursor)
        query.cursor = params.cursor;
    if (params.limit !== undefined)
        query.limit = String(params.limit);
    return kvoxGet(host, '/transcripts', { query });
}
/**
 * `GET /api/transcripts/summary` — the home page's one request (#32).
 *
 * @stability experimental
 */
export async function getTranscriptSummary(host) {
    return kvoxGet(host, '/transcripts/summary');
}
/**
 * `GET /api/transcripts/:id`, conditionally.
 *
 * @stability experimental
 */
export async function getTranscript(host, id, etag = null) {
    return conditionalGet(host, `/transcripts/${encodeURIComponent(id)}`, etag);
}
/**
 * `GET /api/transcripts/:id/segments`, conditionally.
 *
 * @stability experimental
 */
export async function getTranscriptSegments(host, id, etag = null) {
    return conditionalGet(host, `/transcripts/${encodeURIComponent(id)}/segments`, etag);
}
/**
 * `GET /api/transcripts/:id/words` — word timings for ONE window.
 *
 * A window, never the whole transcript: a ten-hour recording's word index is
 * hundreds of megabytes. `toMs` is capped at thirty minutes past `fromMs`
 * server-side and a wider ask is silently narrowed, so the response echoes the
 * window actually served.
 *
 * @stability experimental
 */
export async function getTranscriptWords(host, id, fromMs, toMs) {
    const query = { fromMs: String(Math.max(0, Math.floor(fromMs))) };
    if (toMs !== undefined)
        query.toMs = String(Math.floor(toMs));
    return kvoxGet(host, `/transcripts/${encodeURIComponent(id)}/words`, {
        query,
    });
}
/**
 * `GET /api/transcripts/:id/audio` — a short-lived signed URL.
 *
 * @stability experimental
 */
export async function getTranscriptAudio(host, id) {
    return kvoxGet(host, `/transcripts/${encodeURIComponent(id)}/audio`);
}
// =============================================================================
// Writes
// =============================================================================
/**
 * `POST /api/transcripts` — creates the row AND initialises its upload.
 *
 * @stability experimental
 */
export async function createTranscript(host, input) {
    return kvoxPost(host, '/transcripts', input);
}
/**
 * `PATCH /api/transcripts/:id` — title and/or recording date. Not versioned:
 * both are metadata about the recording, not content of it. The 200 body is
 * the fresh detail, which callers apply directly.
 *
 * @stability experimental
 */
export async function updateTranscript(host, id, input) {
    return kvoxPatch(host, `/transcripts/${encodeURIComponent(id)}`, input);
}
/**
 * `PATCH /api/transcripts/:id` with a title only. Kept for existing callers.
 *
 * @stability experimental
 */
export async function renameTranscript(host, id, title) {
    return updateTranscript(host, id, { title });
}
/**
 * `DELETE /api/transcripts/:id`. Owner only, and there is no path back.
 *
 * @stability experimental
 */
export async function deleteTranscript(host, id) {
    await kvoxDelete(host, `/transcripts/${encodeURIComponent(id)}`);
}
/**
 * `POST /api/transcripts/:id/retry`. Owner only; the stage is derived server-side.
 *
 * @stability experimental
 */
export async function retryTranscript(host, id) {
    return kvoxPost(host, `/transcripts/${encodeURIComponent(id)}/retry`);
}
/**
 * `POST /api/transcripts/:id/cancel`. Owner only.
 *
 * @stability experimental
 */
export async function cancelTranscript(host, id) {
    return kvoxPost(host, `/transcripts/${encodeURIComponent(id)}/cancel`);
}
